import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { clearAuthSession } from "../authSession";

// Componente para renderizar texto con formato
const FormattedText = ({ text }) => {
  if (!text) return null;

  // Dividir el texto por saltos de línea
  const lines = text.split('\n');
  
  return (
    <>
      {lines.map((line, lineIndex) => {
        // Procesar cada línea para encontrar texto en negrita
        const parts = [];
        let lastIndex = 0;
        let key = 0;
        
        // Buscar patrones de texto en negrita (**texto**)
        const boldRegex = /\*\*([^*]+)\*\*/g;
        let match;
        
        while ((match = boldRegex.exec(line)) !== null) {
          // Agregar texto antes del match
          if (match.index > lastIndex) {
            const textBefore = line.substring(lastIndex, match.index);
            // Reemplazar asteriscos individuales por guiones
            const processedText = textBefore.replace(/\*/g, '-');
            parts.push(
              <span key={key++}>
                {processedText}
              </span>
            );
          }
          
          // Agregar texto en negrita
          parts.push(
            <strong key={key++}>
              {match[1]}
            </strong>
          );
          
          lastIndex = match.index + match[0].length;
        }
        
        // Agregar texto restante
        if (lastIndex < line.length) {
          const remainingText = line.substring(lastIndex);
          // Reemplazar asteriscos individuales por guiones
          const processedText = remainingText.replace(/\*/g, '-');
          parts.push(
            <span key={key++}>
              {processedText}
            </span>
          );
        }
        
        return (
          <div key={lineIndex}>
            {parts.length > 0 ? parts : line.replace(/\*/g, '-')}
          </div>
        );
      })}
    </>
  );
};

/**
 * ChatAssistant - Componente de chat que se conecta a un endpoint de streaming
 * 
 * Configuración:
 * - Establece REACT_APP_CHAT_API_URL en tu .env para cambiar el endpoint
 * - Por defecto usa '/api/chat'
 * 
 * Formato esperado del endpoint:
 * - POST request con body: { message: string, conversation_history: array }
 * - Response: streaming con chunks en formato:
 *   - Server-Sent Events: "data: {content: 'texto'}\n"
 *   - JSON directo: {"content": "texto"}
 *   - Texto plano: "texto directo"
 */
export default function ChatAssistant() {
  const navigate = useNavigate();
  
  // Configuración del endpoint - puedes cambiar esta URL según tu API
  const API_ENDPOINT = process.env.REACT_APP_CHAT_API_URL || '/vitisad/chat';
  
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! Soy tu asistente virtual. ¿En qué puedo ayudarte hoy?",
      sender: "assistant",
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const recordingTimeoutRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      const response = await fetch('/vitisad/refresh_token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.accessToken;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error al renovar el token de acceso:", error);
      return null;
    }
  };

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Inicializar el reconocimiento de voz
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'es-ES';
      recognitionRef.current.maxAlternatives = 1;
      
      // Configurar timeout más largo para permitir pausas
      if (recognitionRef.current.continuous !== undefined) {
        recognitionRef.current.continuous = true;
      }

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputValue(transcript);
        
        // Limpiar timeout anterior
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
        }
        
        // Configurar nuevo timeout de 1 segundo para permitir pausas
        recordingTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
          setIsRecording(false);
        }, 1000);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Error en reconocimiento de voz:', event.error);
        setIsRecording(false);
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
        }
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
        if (recordingTimeoutRef.current) {
          clearTimeout(recordingTimeoutRef.current);
        }
      };
    }
  }, []);

  // Cleanup del timeout al desmontar el componente
  useEffect(() => {
    return () => {
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
    };
  }, []);

  const handleSendMessage = async () => {
    const textToSend = inputValue;
    if (!textToSend.trim() || isTyping) return;

    const userMessage = {
      id: Date.now(),
      text: textToSend,
      sender: "user",
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    

    setIsTyping(true);

    // No crear mensaje del asistente vacío todavía, solo mostrar dots
    const assistantMessageId = Date.now() + 1;

    try {

      // Llamar al endpoint de streaming
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        },
        body: JSON.stringify({
          message: textToSend,
          conversation_history: messages.map(msg => ({
            role: msg.sender === "user" ? "user" : "assistant",
            content: msg.text
          })),
          campo_id: null // El backend obtendrá el primer campo del usuario
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Intentar refrescar el token
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken !== null) {
            localStorage.setItem('accessToken', newAccessToken);
            // Hacer la petición nuevamente sin cambiar el estado de typing
            const retryResponse = await fetch(API_ENDPOINT, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${newAccessToken}`
              },
              body: JSON.stringify({
                message: textToSend,
                conversation_history: messages.map(msg => ({
                  role: msg.sender === "user" ? "user" : "assistant",
                  content: msg.text
                })),
                campo_id: null
              })
            });
            
            if (!retryResponse.ok) {
              throw new Error(`Error ${retryResponse.status}: ${await retryResponse.text()}`);
            }
            
            // Continuar con el procesamiento del stream
            const reader = retryResponse.body.getReader();
            const decoder = new TextDecoder();
            let fullResponse = "";
            let buffer = "";
            let pendingUpdates = [];
            let assistantMessageCreated = false;

            // Función para procesar actualizaciones con delay
            const processPendingUpdates = async () => {
              if (pendingUpdates.length === 0) return;
              
              const update = pendingUpdates.shift();
              fullResponse += update.content;
              
              // Si es el primer chunk, crear el mensaje del asistente y quitar el typing
              if (!assistantMessageCreated) {
                const assistantMessage = {
                  id: assistantMessageId,
                  text: fullResponse,
                  sender: "assistant",
                  timestamp: new Date()
                };
                setMessages(prev => [...prev, assistantMessage]);
                setIsTyping(false);
                assistantMessageCreated = true;
              } else {
                // Actualizar el mensaje del asistente con el contenido acumulado
                setMessages(prev => prev.map(msg => 
                  msg.id === assistantMessageId 
                    ? { ...msg, text: fullResponse }
                    : msg
                ));
              }
              
              // Si hay más actualizaciones pendientes, programar la siguiente
              if (pendingUpdates.length > 0) {
                setTimeout(processPendingUpdates, 50);
              }
            };

            while (true) {
              const { done, value } = await reader.read();
              
              if (done) break;

              // Decodificar el chunk y agregarlo al buffer
              buffer += decoder.decode(value, { stream: true });
              
              // Procesar líneas completas
              const lines = buffer.split('\n');
              buffer = lines.pop() || "";

              for (const line of lines) {
                if (line.trim() === '') continue;
                
                // Manejar diferentes formatos de streaming
                let data = null;
                
                if (line.startsWith('data: ')) {
                  try {
                    const jsonData = line.slice(6).trim();
                    if (jsonData === '[DONE]') {
                      continue;
                    }
                    data = JSON.parse(jsonData);
                  } catch (e) {
                    console.error('Error parsing SSE data:', e, 'Line:', line);
                    continue;
                  }
                } else if (line.startsWith('{') && line.endsWith('}')) {
                  try {
                    data = JSON.parse(line);
                  } catch (e) {
                    console.error('Error parsing JSON:', e, 'Line:', line);
                    continue;
                  }
                } else {
                  data = { content: line };
                }

                if (data && data.content) {
                  pendingUpdates.push({ content: data.content });
                  
                  if (pendingUpdates.length === 1) {
                    setTimeout(processPendingUpdates, 0);
                  }
                }
              }
            }

            // Procesar cualquier contenido restante en el buffer
            if (buffer.trim()) {
              pendingUpdates.push({ content: buffer });
              if (pendingUpdates.length === 1) {
                setTimeout(processPendingUpdates, 0);
              }
            }
            
            return; // Salir de la función después del retry exitoso
          } else {
            handleLogout();
            return;
          }
        } else if (response.status === 403) {
          handleLogout();
          return;
        } else {
          const errorText = await response.text();
          console.error('Error del servidor:', errorText);
          throw new Error(`Error ${response.status}: ${errorText}`);
        }
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = "";
      let buffer = "";
      let pendingUpdates = [];

      // Variable para controlar si ya se creó el mensaje del asistente
      let assistantMessageCreated = false;

      // Función para procesar actualizaciones con delay
      const processPendingUpdates = async () => {
        if (pendingUpdates.length === 0) return;
        
        const update = pendingUpdates.shift();
        fullResponse += update.content;
        
        // Si es el primer chunk, crear el mensaje del asistente y quitar el typing
        if (!assistantMessageCreated) {
          const assistantMessage = {
            id: assistantMessageId,
            text: fullResponse,
            sender: "assistant",
            timestamp: new Date()
          };
          setMessages(prev => [...prev, assistantMessage]);
          setIsTyping(false);
          assistantMessageCreated = true;
        } else {
          // Actualizar el mensaje del asistente con el contenido acumulado
          setMessages(prev => prev.map(msg => 
            msg.id === assistantMessageId 
              ? { ...msg, text: fullResponse }
              : msg
          ));
        }
        
        // Si hay más actualizaciones pendientes, programar la siguiente
        if (pendingUpdates.length > 0) {
          setTimeout(processPendingUpdates, 50); // 50ms de delay entre actualizaciones
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        // Decodificar el chunk y agregarlo al buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Procesar líneas completas
        const lines = buffer.split('\n');
        buffer = lines.pop() || ""; // Mantener la línea incompleta en el buffer

        for (const line of lines) {
          if (line.trim() === '') continue;
          
          // Manejar diferentes formatos de streaming
          let data = null;
          
          if (line.startsWith('data: ')) {
            // Formato Server-Sent Events
            try {
              const jsonData = line.slice(6).trim();
              if (jsonData === '[DONE]') {
                continue; // Ignorar marcador de finalización
              }
              data = JSON.parse(jsonData);
            } catch (e) {
              console.error('Error parsing SSE data:', e, 'Line:', line);
              continue;
            }
          } else if (line.startsWith('{') && line.endsWith('}')) {
            // Formato JSON directo
            try {
              data = JSON.parse(line);
            } catch (e) {
              console.error('Error parsing JSON:', e, 'Line:', line);
              continue;
            }
          } else {
            // Texto plano (asumir que es contenido directo)
            data = { content: line };
          }

          if (data && data.content) {
            // Agregar a la cola de actualizaciones pendientes
            pendingUpdates.push({ content: data.content });
            
            // Si es la primera actualización, iniciar el procesamiento
            if (pendingUpdates.length === 1) {
              setTimeout(processPendingUpdates, 0);
            }
          }
        }
      }

      // Procesar cualquier contenido restante en el buffer
      if (buffer.trim()) {
        pendingUpdates.push({ content: buffer });
        if (pendingUpdates.length === 1) {
          setTimeout(processPendingUpdates, 0);
        }
      }

    } catch (error) {
      console.error('Error calling chat API:', error);
      
      // En caso de error, crear mensaje de error y quitar typing
      const errorMessage = {
        id: assistantMessageId,
        text: "Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.",
        sender: "assistant",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsTyping(false);
    } finally {
      setIsTyping(false);
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // No permitir enviar mensaje si el asistente está escribiendo
      if (!isTyping) {
        handleSendMessage();
      }
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert('El reconocimiento de voz no está disponible en este navegador');
      return;
    }

    if (isRecording) {
      // Limpiar timeout si existe
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      // Limpiar cualquier timeout previo
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
      }
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };


  return (
    <div className="chat-container">
      <div className="chat-header">
        <div className="chat-header-left">
          <button 
            onClick={() => {
              // Marcar que viene del chat para usar caché al volver
              sessionStorage.setItem('fromChat', 'true');
              navigate(-1);
            }} 
            className="chat-back-button"
            title="Volver a campos"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
            </svg>
          </button>
        </div>
        <div className="chat-header-left">
          <h3 className="chat-title">Asistente Virtual</h3>
        </div>
        <div className="chat-status">
          <div className="chat-status-dot"></div>
          <span className="chat-status-text">En línea</span>
        </div>
      </div>
      
      <div className="chat-messages-container">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`chat-message ${message.sender === "user" ? "chat-message-user" : "chat-message-assistant"}`}
          >
            <div className={`chat-message-content ${message.sender === "user" ? "chat-message-user-content" : "chat-message-assistant-content"}`}>
              <div className="chat-message-text">
                {message.sender === "assistant" ? (
                  <FormattedText text={message.text} />
                ) : (
                  message.text
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="chat-message chat-message-assistant">
            <div className="chat-message-content chat-message-assistant-content" style={{paddingBottom: '8px', paddingTop: '22px'}}>
              <div class="bouncing-loader">
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      <div className="chat-input-container">
        <textarea
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Escribe tu mensaje aquí..."
          className="chat-text-input"
          rows="1"
        />
        <button
          onClick={toggleRecording}
          disabled={isTyping}
          className={`chat-mic-button ${isRecording ? 'chat-mic-button-recording' : ''}`}
          title={isRecording ? "Detener grabación" : "Iniciar grabación de voz"}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            {isRecording ? (
              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
            ) : (
              <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
            )}
          </svg>
        </button>
        <button
          onClick={handleSendMessage}
          disabled={!inputValue.trim() || isTyping}
          className={`chat-send-button ${inputValue.trim() && !isTyping ? 'chat-send-button-active' : ''}`}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
          </svg>
        </button>
      </div>
    </div>
  );
}

